
import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  CalendarDays,
  CreditCard,
  BriefcaseBusiness,
  Send,
  Download,
  Eye,
  RefreshCw,
  AlertCircle,
  Receipt,
  Clock3,
  ShieldCheck,
} from "lucide-react";

import PartnerHeader from "../../component/PartnerHeader";
import PartnerFooter from "../../component/PartnerFooter";

import "./SubscriptionMan.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "/api";

/* =========================================================
   CURRENT USER
========================================================= */

const getCurrentUserId = () => {
  try {
    const directId =
      localStorage.getItem("userId") ||
      localStorage.getItem("user_id");

    if (directId) {
      return Number(String(directId).replace(/"/g, ""));
    }

    const userRaw = localStorage.getItem("user");

    if (userRaw) {
      const user = JSON.parse(userRaw);

      return Number(
        user?.id ||
          user?.userId ||
          user?.user_id ||
          0
      );
    }
  } catch (error) {
    console.error("Unable to get current user ID:", error);
  }

  return 0;
};

/* =========================================================
   HELPERS
========================================================= */

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const number = (value) =>
  Number(value || 0).toLocaleString("en-IN");

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const getLimitText = (limit) => {
  const value = Number(limit ?? 0);

  if (value < 0) {
    return "Unlimited";
  }

  return number(value);
};

const getRemaining = (limit, used) => {
  const limitValue = Number(limit ?? 0);
  const usedValue = Number(used ?? 0);

  if (limitValue < 0) {
    return "Unlimited";
  }

  return number(
    Math.max(0, limitValue - usedValue)
  );
};

const getPercentage = (limit, used) => {
  const limitValue = Number(limit ?? 0);
  const usedValue = Number(used ?? 0);

  if (limitValue < 0) {
    return 100;
  }

  if (limitValue <= 0) {
    return 0;
  }

  return Math.min(
    100,
    Math.max(
      0,
      (usedValue / limitValue) * 100
    )
  );
};

/* =========================================================
   STATUS
========================================================= */

const getStatusClass = (status) => {
  switch (
    String(status || "").toUpperCase()
  ) {
    case "ACTIVE":
      return "sm-status-active";

    case "EXPIRED":
      return "sm-status-expired";

    case "CANCELLED":
      return "sm-status-cancelled";

    case "PENDING":
      return "sm-status-pending";

    case "FAILED":
      return "sm-status-failed";

    default:
      return "sm-status-default";
  }
};

/* =========================================================
   USAGE CARD
========================================================= */

const UsageCard = ({
  icon: Icon,
  title,
  used,
  limit,
}) => {
  const usedValue = Number(used || 0);
  const limitValue = Number(limit ?? 0);

  const percentage = getPercentage(
    limitValue,
    usedValue
  );

  const unlimited = limitValue < 0;

  return (
    <div className="sm-usage-card">

      <div className="sm-usage-top">

        <div className="sm-usage-icon">
          <Icon size={21} />
        </div>

        <div className="sm-usage-heading">
          <h3>{title}</h3>

          <p>
            {unlimited
              ? `${number(usedValue)} used`
              : `${number(usedValue)} / ${number(
                  limitValue
                )} used`}
          </p>
        </div>

      </div>

      <div className="sm-progress">
        <div
          className="sm-progress-fill"
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>

      <div className="sm-usage-bottom">

        <span>
          Used{" "}
          <strong>
            {number(usedValue)}
          </strong>
        </span>

        <span>
          Remaining{" "}
          <strong>
            {getRemaining(
              limitValue,
              usedValue
            )}
          </strong>
        </span>

      </div>

    </div>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function SubscriptionManagement() {

  const [subscription, setSubscription] =
    useState(null);

  const [usage, setUsage] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const userId = getCurrentUserId();

  /* =======================================================
     FETCH PURCHASED SUBSCRIPTION
  ======================================================= */

  const fetchSubscription = async (
    showRefresh = false
  ) => {

    try {

      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      if (!userId) {
        throw new Error(
          "Unable to identify the logged-in user."
        );
      }

      /*
       * CHANGE THIS URL if your backend endpoint
       * has a different mapping.
       *
       * Example:
       * GET /api/subscription-purchases/user/{userId}
       */

      const response = await fetch(
        `${API_BASE_URL}/subscription-purchases/user/${userId}`
      );

      if (!response.ok) {
        throw new Error(
          `Unable to load subscription (${response.status})`
        );
      }

      const data = await response.json();

      console.log(
        "SUBSCRIPTION PURCHASE RESPONSE:",
        data
      );

      /*
       * Supports:
       *
       * {
       *   subscription: {...},
       *   usage: {...}
       * }
       *
       * OR
       *
       * {
       *   purchase: {...},
       *   usage: {...}
       * }
       *
       * OR directly:
       *
       * {
       *   id: 1,
       *   planName: "...",
       *   ...
       * }
       */

      const purchase =
        data?.subscription ||
        data?.purchase ||
        data?.data ||
        data;

      const usageData =
        data?.usage ||
        data?.subscriptionUsage ||
        data?.usageData ||
        null;

      setSubscription(
        purchase || null
      );

      setUsage(
        usageData || null
      );

    } catch (err) {

      console.error(
        "Unable to load subscription:",
        err
      );

      setSubscription(null);
      setUsage(null);

      setError(
        err?.message ||
          "Unable to load subscription details."
      );

    } finally {

      setLoading(false);
      setRefreshing(false);

    }
  };

  useEffect(() => {
    fetchSubscription();
  }, []);

  /* =======================================================
     MAP LIMITS
  ======================================================= */

  const limits = useMemo(() => {

    if (!subscription) {
      return {
        resume: 0,
        jobPost: 0,
        jobInvite: 0,
        profileView: 0,
      };
    }

    return {

      resume:
        subscription.resumeLimit ??
        subscription.resume_limit ??
        0,

      jobPost:
        subscription.jobPostLimit ??
        subscription.job_post_limit ??
        0,

      jobInvite:
        subscription.jobInviteLimit ??
        subscription.job_invite_limit ??
        0,

      profileView:
        subscription.profileViewLimit ??
        subscription.profile_view_limit ??
        0,
    };

  }, [subscription]);

  /* =======================================================
     MAP USAGE
  ======================================================= */

  const usageValues = useMemo(() => {

    /*
     * subscription_usage table data
     *
     * Supports camelCase and snake_case.
     */

    return {

      resumeUsed:
        usage?.resumeUsed ??
        usage?.resume_used ??
        subscription?.resumeUsed ??
        subscription?.resume_used ??
        0,

      jobPostUsed:
        usage?.jobPostUsed ??
        usage?.job_post_used ??
        subscription?.jobPostUsed ??
        subscription?.job_post_used ??
        0,

      jobInviteUsed:
        usage?.jobInviteUsed ??
        usage?.job_invite_used ??
        subscription?.jobInviteUsed ??
        subscription?.job_invite_used ??
        0,

      profileViewUsed:
        usage?.profileViewUsed ??
        usage?.profile_view_used ??
        subscription?.profileViewUsed ??
        subscription?.profile_view_used ??
        0,
    };

  }, [usage, subscription]);

  /* =======================================================
     LOADING
  ======================================================= */

  if (loading) {

    return (
      <>
        <PartnerHeader />

        <div className="sm-page">

          <div className="sm-loading">

            <div className="sm-spinner" />

            <p>
              Loading subscription...
            </p>

          </div>

        </div>

        <PartnerFooter />
      </>
    );
  }

  /* =======================================================
     PAGE
  ======================================================= */

  return (
    <>
      <PartnerHeader />

      <main className="sm-page">

        <div className="sm-container">

          {/* ==============================================
              PAGE HEADER
          ============================================== */}

          <div className="sm-page-header">

            <div>

              <span className="sm-kicker">
                Subscription
              </span>

              <h1>
                Subscription Management
              </h1>

              <p>
                Manage your purchased plan,
                subscription validity and usage.
              </p>

            </div>

            <button
              type="button"
              className="sm-refresh-btn"
              onClick={() =>
                fetchSubscription(true)
              }
              disabled={refreshing}
            >

              <RefreshCw
                size={17}
                className={
                  refreshing
                    ? "sm-spin"
                    : ""
                }
              />

              {refreshing
                ? "Refreshing..."
                : "Refresh"}

            </button>

          </div>

          {/* ==============================================
              ERROR
          ============================================== */}

          {error && (

            <div className="sm-error">

              <AlertCircle size={21} />

              <div>

                <strong>
                  Unable to load subscription
                </strong>

                <p>
                  {error}
                </p>

              </div>

              <button
                type="button"
                onClick={() =>
                  fetchSubscription()
                }
              >
                Try Again
              </button>

            </div>

          )}

          {/* ==============================================
              NO PLAN
          ============================================== */}

          {!error &&
            !subscription && (

              <div className="sm-empty">

                <div className="sm-empty-icon">
                  <CreditCard size={35} />
                </div>

                <h2>
                  No Purchased Plan
                </h2>

                <p>
                  You have not purchased any
                  subscription plan yet.
                </p>

              </div>

            )}

          {/* ==============================================
              PURCHASED PLAN
          ============================================== */}

          {subscription && (

            <>

              {/* ==========================================
                  PURCHASED PLAN CARD
              ========================================== */}

              <section className="sm-plan-card">

                <div className="sm-plan-header">

                  <div>

                    <span className="sm-plan-label">
                      PURCHASED PLAN
                    </span>

                    <h2>
                      {subscription.planName ||
                        subscription.plan_name ||
                        "Subscription Plan"}
                    </h2>

                    <p className="sm-plan-code">
                      {subscription.planCode ||
                        subscription.plan_code ||
                        "-"}
                    </p>

                  </div>

                  <div
                    className={`sm-status ${getStatusClass(
                      subscription.status
                    )}`}
                  >

                    <CheckCircle2
                      size={17}
                    />

                    {subscription.status ||
                      "ACTIVE"}

                  </div>

                </div>

                {/* PLAN FINANCIAL DETAILS */}

                <div className="sm-plan-info">

                  <div>

                    <span>
                      Plan Price
                    </span>

                    <strong>
                      {money(
                        subscription.price
                      )}
                    </strong>

                  </div>

                  <div>

                    <span>
                      GST
                    </span>

                    <strong>
                      {money(
                        subscription.gstAmount ??
                          subscription.gst_amount
                      )}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Discount
                    </span>

                    <strong>
                      {money(
                        subscription.discountAmount ??
                          subscription.discount_amount
                      )}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Total Paid
                    </span>

                    <strong>
                      {money(
                        subscription.totalAmount ??
                          subscription.total_amount
                      )}
                    </strong>

                  </div>

                </div>

              </section>

              {/* ==========================================
                  SUBSCRIPTION DATES
              ========================================== */}

              <section className="sm-info-section">

                <div className="sm-section-title">

                  <div className="sm-title-icon">
                    <CalendarDays size={20} />
                  </div>

                  <div>

                    <h2>
                      Subscription Period
                    </h2>

                    <p>
                      Your purchased plan validity.
                    </p>

                  </div>

                </div>

                <div className="sm-date-grid">

                  <div className="sm-info-box">

                    <span>
                      Purchase Date
                    </span>

                    <strong>
                      {formatDate(
                        subscription.purchaseDate ??
                          subscription.purchase_date
                      )}
                    </strong>

                  </div>

                  <div className="sm-info-box">

                    <span>
                      Valid From
                    </span>

                    <strong>
                      {formatDate(
                        subscription.startDate ??
                          subscription.start_date
                      )}
                    </strong>

                  </div>

                  <div className="sm-info-box">

                    <span>
                      Valid Until
                    </span>

                    <strong>
                      {formatDate(
                        subscription.expiryDate ??
                          subscription.expiry_date
                      )}
                    </strong>

                  </div>

                  <div className="sm-info-box">

                    <span>
                      Payment Status
                    </span>

                    <strong>
                      {subscription.paymentStatus ??
                        subscription.payment_status ??
                        "PAID"}
                    </strong>

                  </div>

                </div>

              </section>

              {/* ==========================================
                  USAGE
              ========================================== */}

              <section className="sm-usage-section">

                <div className="sm-section-title">

                  <div className="sm-title-icon">
                    <ShieldCheck size={20} />
                  </div>

                  <div>

                    <h2>
                      Subscription Usage
                    </h2>

                    <p>
                      Current usage mapped from
                      your subscription usage record.
                    </p>

                  </div>

                </div>

                <div className="sm-usage-grid">

                  <UsageCard
                    icon={BriefcaseBusiness}
                    title="Job Posts"
                    used={
                      usageValues.jobPostUsed
                    }
                    limit={
                      limits.jobPost
                    }
                  />

                  <UsageCard
                    icon={Send}
                    title="Job Invites"
                    used={
                      usageValues.jobInviteUsed
                    }
                    limit={
                      limits.jobInvite
                    }
                  />

                  <UsageCard
                    icon={Download}
                    title="Resume Downloads"
                    used={
                      usageValues.resumeUsed
                    }
                    limit={
                      limits.resume
                    }
                  />

                  <UsageCard
                    icon={Eye}
                    title="Profile Views"
                    used={
                      usageValues.profileViewUsed
                    }
                    limit={
                      limits.profileView
                    }
                  />

                </div>

              </section>

              {/* ==========================================
                  PLAN FEATURES
              ========================================== */}

              <section className="sm-features-card">

                <div className="sm-section-title">

                  <div className="sm-title-icon">
                    <CheckCircle2 size={20} />
                  </div>

                  <div>

                    <h2>
                      Plan Includes
                    </h2>

                    <p>
                      Limits included with your
                      purchased subscription.
                    </p>

                  </div>

                </div>

                <div className="sm-feature-grid">

                  <div className="sm-feature">

                    <CheckCircle2 size={18} />

                    <div>

                      <span>
                        Job Posts
                      </span>

                      <strong>
                        {getLimitText(
                          limits.jobPost
                        )}
                      </strong>

                    </div>

                  </div>

                  <div className="sm-feature">

                    <CheckCircle2 size={18} />

                    <div>

                      <span>
                        Job Invites
                      </span>

                      <strong>
                        {getLimitText(
                          limits.jobInvite
                        )}
                      </strong>

                    </div>

                  </div>

                  <div className="sm-feature">

                    <CheckCircle2 size={18} />

                    <div>

                      <span>
                        Resume Downloads
                      </span>

                      <strong>
                        {getLimitText(
                          limits.resume
                        )}
                      </strong>

                    </div>

                  </div>

                  <div className="sm-feature">

                    <CheckCircle2 size={18} />

                    <div>

                      <span>
                        Profile Views
                      </span>

                      <strong>
                        {getLimitText(
                          limits.profileView
                        )}
                      </strong>

                    </div>

                  </div>

                </div>

              </section>

              {/* ==========================================
                  PURCHASE DETAILS
              ========================================== */}

              <section className="sm-details-card">

                <div className="sm-section-title">

                  <div className="sm-title-icon">
                    <Receipt size={20} />
                  </div>

                  <div>

                    <h2>
                      Purchase Details
                    </h2>

                    <p>
                      Complete information about
                      this subscription purchase.
                    </p>

                  </div>

                </div>

                <div className="sm-details-grid">

                  <div>

                    <span>
                      Subscription ID
                    </span>

                    <strong>
                      #
                      {subscription.id}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Product ID
                    </span>

                    <strong>
                      #
                      {subscription.subscriptionProductId ??
                        subscription.subscription_product_id ??
                        "-"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      User ID
                    </span>

                    <strong>
                      #
                      {subscription.userId ??
                        subscription.user_id ??
                        userId}
                    </strong>

                  </div>

                  <div>

                    <span>
                      User Type
                    </span>

                    <strong>
                      {subscription.userType ??
                        subscription.user_type ??
                        "PARTNER"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Payment Method
                    </span>

                    <strong>
                      {subscription.paymentMethod ??
                        subscription.payment_method ??
                        "-"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Transaction ID
                    </span>

                    <strong>
                      {subscription.transactionId ??
                        subscription.transaction_id ??
                        "-"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Payment Status
                    </span>

                    <strong>
                      {subscription.paymentStatus ??
                        subscription.payment_status ??
                        "-"}
                    </strong>

                  </div>

                  <div>

                    <span>
                      Purchase Time
                    </span>

                    <strong>
                      {formatDateTime(
                        subscription.purchaseDate ??
                          subscription.purchase_date
                      )}
                    </strong>

                  </div>

                </div>

              </section>

              {/* ==========================================
                  QUICK SUMMARY
              ========================================== */}

              <section className="sm-summary-card">

                <div className="sm-summary-item">

                  <BriefcaseBusiness size={20} />

                  <div>

                    <span>
                      Jobs Remaining
                    </span>

                    <strong>
                      {getRemaining(
                        limits.jobPost,
                        usageValues.jobPostUsed
                      )}
                    </strong>

                  </div>

                </div>

                <div className="sm-summary-item">

                  <Send size={20} />

                  <div>

                    <span>
                      Invites Remaining
                    </span>

                    <strong>
                      {getRemaining(
                        limits.jobInvite,
                        usageValues.jobInviteUsed
                      )}
                    </strong>

                  </div>

                </div>

                <div className="sm-summary-item">

                  <Download size={20} />

                  <div>

                    <span>
                      Downloads Remaining
                    </span>

                    <strong>
                      {getRemaining(
                        limits.resume,
                        usageValues.resumeUsed
                      )}
                    </strong>

                  </div>

                </div>

                <div className="sm-summary-item">

                  <Eye size={20} />

                  <div>

                    <span>
                      Views Remaining
                    </span>

                    <strong>
                      {getRemaining(
                        limits.profileView,
                        usageValues.profileViewUsed
                      )}
                    </strong>

                  </div>

                </div>

              </section>

              {/* ==========================================
                  VALIDITY NOTICE
              ========================================== */}

              <div className="sm-notice">

                <Clock3 size={19} />

                <div>

                  <strong>
                    Subscription validity
                  </strong>

                  <p>
                    Your usage is available only
                    while your subscription is active
                    and within its validity period.
                  </p>

                </div>

              </div>

            </>
          )}

        </div>

      </main>

      <PartnerFooter />
    </>
  );
}
